import get from 'lodash.get';
import React, { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { IconType } from 'react-icons/lib';
import { toast } from 'react-toastify';

import apiUtil from 'src/api/payments';
import CustomToast from 'src/components/CustomToast';
import AreYouSureModal from 'src/components/ExternalConfirmationModal copy';
import BankCardsIcon from 'src/components/Icon/BankCardsIcon';
import NoteAndCoinsIcon from 'src/components/Icon/NoteAndCoinsIcon';
import Loader from 'src/components/Loader';
import { AppContext } from 'src/containers/Core/AppContext';
import { AppState } from 'src/containers/Core/AppReducer';
import { DELETE_ACCOUNT_ERROR } from 'src/containers/Core/Messages';
import { BUSINESS_ONBOARDING_STEP } from '..';
import SetupBankAccount from './SetupBankAccount';
import SetupCardDetails from './SetupCardDetails';

import './style.scss';

type BankDetailsProps = {
  setOnboardingStep: (step: BUSINESS_ONBOARDING_STEP) => void;
};

const BankDetails: React.FunctionComponent<BankDetailsProps> = ({ setOnboardingStep }) => {
  const [paymentType, setPaymentType] = useState<PaymentType>(null);
  const [bankDetails, setBankDetails] = useState<BankDetails>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [deletingBankAccount, setDeletingBankAccount] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string>(null);

  const { store } = useContext(AppContext);
  const { businessOnboardingDetails }: AppState = store;
  const businessUuid = get(businessOnboardingDetails, 'business.uuid');

  const switchAccountTypeMessage = `The entered information or added account will be lost if you switch.
  Are you sure you want to switch?`;
  const deleteBankAccountMessage = `The added account will be deleted. Are you sure you want to delete?`;

  const onDeleteClick = () => {
    setShowDeleteModal(deleteBankAccountMessage);
  };

  const handleDelete = async () => {
    setDeletingBankAccount(true);
    try {
      await apiUtil.deleteBankAccount(businessUuid, bankDetails.bankAccountId);
      setBankDetails(null);
      setShowDeleteModal(null);
    } catch (error) {
      toast(<CustomToast description={DELETE_ACCOUNT_ERROR} />);
    } finally {
      setDeletingBankAccount(false);
    }
  };

  const fetchDefaultAccount = async () => {
    setLoading(true);

    try {
      const response = await apiUtil.getDefaultAccount(businessUuid);
      if (response) {
        setPaymentType(response.type);
        if (response.type === 'bank_account') {
          setBankDetails(response as BankDetails);
        }
      }
    } catch (error) {
      setPaymentType('bank_account');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDefaultAccount();
  }, [businessOnboardingDetails]);

  const handleAccountTypeTileClick = (type: PaymentType) => {
    if (paymentType === 'bank_account' && type === 'card' && bankDetails) {
      setShowDeleteModal(switchAccountTypeMessage);
    } else {
      setPaymentType(type);
    }
  };

  const setUpOnboardingBankAccount = async () => {
    try {
      await apiUtil.setUpOnboardingBankAccount(businessOnboardingDetails.uuid);
    } catch (error) {
      // handle error
    }
  };

  const handleSuccess = () => {
    setUpOnboardingBankAccount();
    setOnboardingStep(BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_ACTIVATION);
  };

  if (!businessUuid) {
    return null;
  }

  const renderContent = () => {
    if (paymentType === 'bank_account') {
      return (
        <SetupBankAccount
          accountDetails={bankDetails}
          setAccountDetails={setBankDetails}
          onDeleteClick={onDeleteClick}
          onVerifySuccess={handleSuccess}
        />
      );
    }

    return <SetupCardDetails onSuccess={handleSuccess} />;
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="overflow-auto p-3" style={{ height: 'calc(100% - 54px)' }}>
      <AreYouSureModal
        confirmationMessage={showDeleteModal}
        show={!!showDeleteModal}
        onHide={() => setShowDeleteModal(null)}
        onConfirmClick={() => {
          handleDelete();
          setPaymentType(showDeleteModal === switchAccountTypeMessage ? 'card' : 'bank_account');
        }}
        submitting={deletingBankAccount}
      />
      <div className="font-size-15 font-weight-bold">Choose Your Account Type</div>
      <div className="d-flex">
        <AccountTypeTile
          Icon={NoteAndCoinsIcon}
          title="Bank Account"
          description="Processing fee of 0.8% to a maximum of $5 will be charged for top-ups"
          isActive={paymentType === 'bank_account'}
          onClick={() => handleAccountTypeTileClick('bank_account')}
          className="mr-3 my-3"
        />
        <div className="align-self-center font-weight-bold font-size-15">OR</div>
        <AccountTypeTile
          Icon={BankCardsIcon}
          title="Credit/Debit Cards"
          description="Processing fee of 3.5% will be charged for top-ups"
          isActive={paymentType === 'card'}
          onClick={() => handleAccountTypeTileClick('card')}
          className="m-3"
        />
      </div>
      <Row className="mb-3 text-primary medium">
        <Col xs={12}>
          You will not be charged until the sign up process is complete and you join a community. <br />
          If the community you join has sponsored funds, you will not be charged until the sponsored funds deplete.
        </Col>
      </Row>
      {renderContent()}
    </div>
  );
};

type AccountTypeTileProps = {
  Icon: IconType;
  title: string;
  description: string;
  isActive: boolean;
  onClick: () => void;
  className?: string;
};

const AccountTypeTile: React.FC<AccountTypeTileProps> = ({
  Icon,
  title,
  description,
  isActive,
  onClick,
  className,
}) => (
  <div
    className={`account-type-tile d-flex flex-column border rounded py-3 px-4 text-center c-pointer ${
      isActive ? 'royal-blue-color-border' : ''
    } ${className || ''}`}
    onClick={onClick}
  >
    <div>
      <Icon width={50} height={50} />
    </div>
    <div className="text-primary font-size-14 my-1">{title}</div>
    <div className="text-muted small">{description}</div>
  </div>
);

export default BankDetails;
